import { Component, computed, inject, Input, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

import { IModal } from '../../../../components/modals/modal.interface';
import { ModalContainerComponent } from '../../../../components/modals/modal-container.component';
import { ICreateTenantRequest } from '../../../../../../shared/interfaces/tenant.interface';
import { $privacySettings, $tenant } from '../../../../../../shared/constants/tenant';
import { $academy } from '../../../../../../shared/constants/academy';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { $salesforceAccount } from '../../../../../../shared/constants/salesforce';
import { ISalesforceAccount } from '../../../../../../shared/interfaces/salesforce.interface';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';

export interface ICreateTenantConfirmationModalContext {
  tenantDetails: ICreateTenantRequest;
  packageName: string;
  tenantBaseUrl: string;
}

@Component({
  selector: 'app-create-tenant-confirmation-modal',
  templateUrl: './create-tenant-confirmation-modal.component.html',
  standalone: false,
})
export class CreateTenantConfirmationModalComponent implements IModal {
  public $tenant: typeof $tenant = $tenant;
  public $academy: typeof $academy = $academy;
  public readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;

  @ViewChild(ModalContainerComponent, { static: true })
  public container: ModalContainerComponent;

  @Input() context: ICreateTenantConfirmationModalContext;
  public confirmed: boolean = false;

  private featureFlagService: FeatureFlagService = inject(FeatureFlagService);

  public readonly isAvailableConfidentialScanning: Signal<boolean> = toSignal<boolean>(
    this.featureFlagService.variation$(FeatureFlagCollection.digitalAssetsPrivacySetting, false).pipe(map(Boolean)),
  );

  public readonly shouldShowMarginBottomForAcademySeats: Signal<boolean> = computed((): boolean => {
    return (
      SharedCommonUtility.notNullish(this.context?.tenantDetails?.[$tenant.userWayWebsiteLimit]) ||
      this.isAvailableConfidentialScanning()
    );
  });

  public closeModal(confirmed: boolean): void {
    this.confirmed = confirmed;
    this.container.closeModal();
  }

  public getTenantName(): string {
    return this.context.tenantDetails?.[$tenant.name];
  }

  public getSalesforceAccount(): string {
    const tenantName: string = this.context.tenantDetails?.[$tenant.name];
    const sfAccount: ISalesforceAccount = this.context.tenantDetails?.[$tenant.salesforceAccount];
    if (SharedCommonUtility.isNullish(sfAccount) || sfAccount[$salesforceAccount.name] === tenantName) {
      return undefined;
    }

    return `${sfAccount[$salesforceAccount.name]} (${sfAccount[$salesforceAccount.id]})`;
  }

  public getConfidentialScanningEnabled(): boolean {
    return this.context.tenantDetails?.[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled] ?? false;
  }
}
